import { useEffect, useCallback, useMemo, useState } from 'react';

import axios, { endpoints, formatEndpoint } from 'src/utils/axios';

import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { AuthStateType, AuthUserSessionType } from '../types';

const initialState: AuthStateType = {
  session: null,
  loading: true,
};

export const STORAGE_KEY = 'accessToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, setState] = useState(initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get(endpoints.auth.current);

        const { session } = response.data;

        setState({
          loading: false,
          session
        })
      } else {
        setState({
          loading: false,
          session: null
        })
      }
    } catch (error) {
      console.error(error);

      setState({
        loading: false,
        session: null
      })
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const data = {
      email,
      password,
    };

    const response = await axios.post(endpoints.auth.login, data);

    const { accessToken, session } = response.data;
    setSession(accessToken);

    setState({
      ...state,
      session
    })

    return session
  }, [state]);

  const passwordUpdated = useCallback((accessToken: string, session: AuthUserSessionType) => {
    setSession(accessToken);

    setState(s => ({
      ...s,
      session
    }))
  }, [setState]);


  const changeOrganization = useCallback(async (organizationId: number) => {
    const response = await axios.post(formatEndpoint(endpoints.auth.changeOrganization, {
      targetOrganizationId: organizationId,
    }));

    const { accessToken, session } = response.data;
    setSession(accessToken);

    setState({
      ...state,
      session
    })

    return session
  }, [state]);


  // REGISTER
  const register = useCallback(

    async (name: string, phone: string, email: string, password: string, iftaOnly: boolean, organizationName: string) => {
      const data = {
        name,
        phone,
        email,
        password,
        iftaOnly,
        organizationName,
      };

      const response = await axios.post(endpoints.auth.register, data);

      const { accessToken, session } = response.data;

      localStorage.setItem(STORAGE_KEY, accessToken);

      setState({
        ...state,
        session
      })
    },
    [state]
  );

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);

    setState({
      ...state,
      session: null
    })
  }, [state]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.session ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      session: state.session,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      changeOrganization,
      register,
      logout,
      passwordUpdated,
    }),
    [login, logout, passwordUpdated, changeOrganization, register, state.session, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
